import { cn } from '../../lib/utils';
import CategoryIcon, { getCategoryLabel } from '../shared/CategoryIcon';

/**
 * Badge - Lucky Number Design System
 *
 * Status indicators for raffles, categories, and other labels.
 * Dark-first design with accent colors.
 */

const variants = {
  // Status badges (for dark backgrounds)
  default: 'bg-midnight-lighter text-cream/80 border border-cream/10',
  primary: 'bg-primary/20 text-primary-light border border-primary/30',
  secondary: 'bg-petrol/30 text-cream border border-petrol/40',
  success: 'bg-success/20 text-success border border-success/30',
  warning: 'bg-warning/20 text-warning border border-warning/30',
  error: 'bg-error/20 text-error border border-error/30',

  // Raffle status variants
  active: 'bg-accent/20 text-accent border border-accent/40',
  ending: 'bg-warning/20 text-warning border border-warning/40',
  urgent: 'bg-error/20 text-error border border-error/40 animate-pulse-urgency',
  ended: 'bg-midnight-lighter text-cream/50 border border-cream/10',
  cancelled: 'bg-error/10 text-error/70 border border-error/20',
  soldOut: 'bg-midnight-lighter text-cream/50 border border-cream/10',

  // Category variants - teal accent for visibility
  category: 'bg-primary/10 text-primary-light border border-primary/30',

  // Condition variants
  'condition-new': 'bg-success/20 text-success border border-success/40',
  'condition-likeNew': 'bg-primary/20 text-primary-light border border-primary/40',
  'condition-good': 'bg-midnight-lighter text-cream/80 border border-cream/20',
  'condition-fair': 'bg-warning/20 text-warning border border-warning/40',

  // Gold variant for special items
  gold: 'bg-accent/20 text-accent border border-accent/40',

  // Outline variants
  outline: 'border border-cream/40 text-cream/80 bg-transparent',
  'outline-primary': 'border border-primary/50 text-primary bg-transparent',
  'outline-gold': 'border border-accent/50 text-accent bg-transparent',
  'outline-success': 'border border-success/50 text-success bg-transparent',

  // Light variants (for light backgrounds)
  'light-default': 'bg-midnight/10 text-midnight/70 border border-midnight/10',
  'light-active': 'bg-petrol/10 text-petrol border border-petrol/20',
  'light-success': 'bg-success/10 text-success-dark border border-success/20',
  'light-warning': 'bg-warning/10 text-warning-dark border border-warning/20',
  'light-error': 'bg-error/10 text-error-dark border border-error/20',
};

const sizes = {
  sm: 'px-2 py-0.5 text-2xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

export default function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon: Icon,
  pulse = false,
  className,
  ...props
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded-full',
        variants[variant],
        sizes[size],
        pulse && 'animate-pulse-urgency',
        className
      )}
      {...props}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
}

/**
 * StatusBadge
 *
 * Pre-configured badge for raffle status display.
 */
export function StatusBadge({ status, endsAt, className }) {
  // Determine urgency based on end time
  const isEndingSoon = endsAt && new Date(endsAt) - new Date() < 24 * 60 * 60 * 1000;
  const isEndingVerySoon = endsAt && new Date(endsAt) - new Date() < 60 * 60 * 1000;

  const statusConfig = {
    draft: { label: 'Draft', variant: 'default' },
    pending: { label: 'Pending', variant: 'warning' },
    active: {
      label: isEndingVerySoon ? 'Final Hour!' : isEndingSoon ? 'Ending Soon' : 'Live',
      variant: isEndingVerySoon ? 'urgent' : isEndingSoon ? 'ending' : 'active',
    },
    completed: { label: 'Ended', variant: 'ended' },
    cancelled: { label: 'Cancelled', variant: 'cancelled' },
    drawing: { label: 'Drawing...', variant: 'gold', pulse: true },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <Badge
      variant={config.variant}
      pulse={config.pulse}
      className={className}
    >
      {config.label}
    </Badge>
  );
}

/**
 * CategoryBadge
 *
 * Badge for raffle categories with icon.
 */
export function CategoryBadge({ category, light = false, size = 'sm', showIcon = true, className }) {
  // Map badge sizes to icon sizes
  const iconSize = size === 'lg' ? 'sm' : 'xs';

  return (
    <Badge
      variant={light ? 'light-default' : 'category'}
      size={size}
      className={className}
    >
      {showIcon && <CategoryIcon category={category} size={iconSize} />}
      {getCategoryLabel(category)}
    </Badge>
  );
}

/**
 * ConditionBadge
 *
 * Badge for item condition.
 */
export function ConditionBadge({ condition, light = false, className }) {
  const conditionConfig = {
    new: { label: 'New', variant: light ? 'light-success' : 'success' },
    likeNew: { label: 'Like New', variant: light ? 'light-active' : 'primary' },
    good: { label: 'Good', variant: light ? 'light-default' : 'default' },
    fair: { label: 'Fair', variant: light ? 'light-warning' : 'warning' },
  };

  const config = conditionConfig[condition] || conditionConfig.good;

  return (
    <Badge variant={config.variant} size="sm" className={className}>
      {config.label}
    </Badge>
  );
}

/**
 * VerifiedBadge
 *
 * Verified seller indicator.
 */
export function VerifiedBadge({ light = false, className }) {
  return (
    <Badge
      variant={light ? 'light-success' : 'success'}
      size="sm"
      className={className}
    >
      <svg
        className="w-3 h-3"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      Verified
    </Badge>
  );
}

/**
 * FeaturedBadge
 *
 * Featured item indicator with accent styling.
 */
export function FeaturedBadge({ className }) {
  return (
    <Badge variant="gold" size="sm" className={className}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Featured
    </Badge>
  );
}

/**
 * WinnerBadge
 *
 * Badge for winner status with accent glow.
 */
export function WinnerBadge({ className }) {
  return (
    <Badge
      variant="gold"
      size="lg"
      className={cn('shadow-glow-accent animate-accent-pulse', className)}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
      </svg>
      Winner
    </Badge>
  );
}
